import { useLayoutEffect, useRef } from "react";

import { DocumentBrowserLayout } from "@/api/document/browser-layout";
import type { DocumentFloatLayer } from "@/api/document/browser-layout";
import { FloatingPlacement } from "@/api/document/floats";
import type { FloatImage } from "@/api/document/floats";
import { A4_METRICS, paginate } from "@/api/document/paginate";
import type { TermMargins, TermTypography } from "@/api/document/render";
import { documentStyles } from "@/api/document/styles";

import { DocumentPageStyle } from "./page-style";
import { PAGE_GAP_BAND } from "./pagination";

const PREVIEW_PADDING = 24;

export function PagedDocumentPreview({
	html,
	floats,
	pageIds,
	margins,
	typography,
}: {
	html: string;
	floats: FloatImage[];
	pageIds: string[];
	margins: TermMargins;
	typography: TermTypography;
}) {
	const viewportRef = useRef<HTMLDivElement>(null);
	const stageRef = useRef<HTMLDivElement>(null);
	const canvasRef = useRef<HTMLDivElement>(null);
	const hostRef = useRef<HTMLDivElement>(null);
	const scaleRef = useRef(1);
	const pageCountRef = useRef(1);

	useLayoutEffect(() => {
		const viewport = viewportRef.current;

		if (viewport === null) {
			return;
		}

		const resize = () => {
			const stage = stageRef.current;
			const canvas = canvasRef.current;

			if (stage === null || canvas === null) {
				return;
			}

			const scale = Math.min(
				1,
				Math.max(0.1, (viewport.clientWidth - PREVIEW_PADDING * 2) / A4_METRICS.pageWidth),
			);
			const height =
				pageCountRef.current * A4_METRICS.pageHeight + (pageCountRef.current - 1) * PAGE_GAP_BAND;

			scaleRef.current = scale;
			canvas.style.transform = `scale(${scale})`;
			stage.style.width = `${A4_METRICS.pageWidth * scale}px`;
			stage.style.height = `${height * scale}px`;
		};
		const observer = new ResizeObserver(resize);

		observer.observe(viewport);
		resize();

		return () => observer.disconnect();
	}, []);

	useLayoutEffect(() => {
		const host = hostRef.current;

		if (host === null) {
			return;
		}

		host.innerHTML = `<section class="doc-flow">${html}</section>`;
		let canceled = false;

		const layout = async () => {
			const images = [...host.querySelectorAll("img")];
			const loaded = images.map(async (image) => {
				if (image.complete) {
					return;
				}

				await new Promise<void>((resolve) => {
					image.addEventListener("load", () => resolve(), { once: true });
					image.addEventListener("error", () => resolve(), { once: true });
				});
			});

			await Promise.all([document.fonts.ready, ...loaded]);

			if (canceled) {
				return;
			}

			const canvas = canvasRef.current;

			if (canvas === null) {
				return;
			}

			canvas.style.transform = "none";

			const flow = host.querySelector(".doc-flow");

			if (flow === null) {
				throw new Error("doc_flow_not_found");
			}

			const measured = DocumentBrowserLayout.measureChildren(flow);
			const groups = paginate(
				measured.blocks,
				A4_METRICS.pageHeight - margins.top - margins.bottom,
				{ guardOversized: true },
			).map((indexes) => indexes.map((index) => measured.ids[index]).filter((id) => id !== ""));

			DocumentBrowserLayout.applyPages(groups, host);

			const paints = FloatingPlacement.resolveFlow(groups, pageIds, floats);
			const layers: DocumentFloatLayer[][] = paints.map((page) =>
				page.map((float) => ({
					src: `/terms/image/${float.file}`,
					x: float.x,
					y: float.y,
					width: float.width,
					height: float.height,
				})),
			);

			DocumentBrowserLayout.applyFloats(layers, host);
			canvas.style.transform = `scale(${scaleRef.current})`;
			host.dataset.pages = String(groups.length);
			host.dataset.pageBlocks = JSON.stringify(groups);
			pageCountRef.current = groups.length;

			const stage = stageRef.current;

			if (stage !== null) {
				const height = groups.length * A4_METRICS.pageHeight + (groups.length - 1) * PAGE_GAP_BAND;

				stage.style.height = `${height * scaleRef.current}px`;
			}
		};

		void layout();

		return () => {
			canceled = true;
		};
	}, [floats, html, margins.bottom, margins.top, pageIds]);

	return (
		<div
			ref={viewportRef}
			data-slot="live-document"
			className="min-h-0 flex-1 overflow-auto bg-background/60 p-6"
		>
			<style>{documentStyles.term}</style>
			<style>{PREVIEW_STYLES}</style>
			<div
				ref={stageRef}
				className="relative mx-auto"
				style={{ width: A4_METRICS.pageWidth, height: A4_METRICS.pageHeight }}
			>
				<div
					ref={canvasRef}
					data-component="document-editor"
					data-read-only="true"
					className="doc-term doc-preview-canvas absolute top-0 left-0 origin-top-left"
					style={{
						...DocumentPageStyle.preview(margins, typography),
					}}
				>
					<div ref={hostRef} />
				</div>
			</div>
		</div>
	);
}

const PREVIEW_STYLES = `.doc-preview-canvas{width:${A4_METRICS.pageWidth}px}
.doc-preview-canvas .doc-page{margin-bottom:${PAGE_GAP_BAND}px;box-shadow:0 2px 14px rgba(26,26,26,.16)}
.doc-preview-canvas .doc-page:last-child{margin-bottom:0}`;
